import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Table, Spinner, Alert, Form, Badge, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { auditLogService } from '../../services/api';

/**
 * Dashboard per amministratori per visualizzare tutte le attività di audit
 */
const AuditDashboard = () => {
    const [recentLogs, setRecentLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filters, setFilters] = useState({
        entityType: '',
        limit: 50
    });

    const fetchData = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            // Fetch recent logs using the audit log service
            const response = await auditLogService.getRecentLogs(
                filters.limit,
                filters.entityType || null
            );

            if (response.success) {
                const logs = response.data || [];
                logs.forEach(log => {
                    log.changedFields = getChangedFieldsDetails(log.changedFields);
                });
                setRecentLogs(logs);
            } else {
                setError(response.message || 'Errore nel caricamento dei dati di audit');
            }

        } catch (err) {
            console.error('Error fetching audit data:', err);
            setError('Errore nel caricamento dei dati di audit');
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleString('it-IT', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        }).replace(',', '');
    };

    const getActionBadge = (log) => {
        return (
            <OverlayTrigger
                placement="top"
                overlay={
                    <Tooltip>
                        <div>
                            <strong>Campi modificati:</strong>
                            {log.changedFields.map((detail, index) => (
                                <div key={index} style={{ fontSize: '0.8em', marginTop: '2px' }}>
                                    <strong>{detail.field}:</strong> {detail.oldValue || 'null'} → {detail.newValue || 'null'}
                                </div>
                            ))}
                        </div>
                    </Tooltip>
                }
            >
                <Badge bg={{
                    ADDED: 'success',
                    MODIFIED: 'warning',
                    DELETED: 'danger'
                }[log.action] || 'secondary'}>
                    {log.action}
                </Badge>
            </OverlayTrigger>
        );
    };

    const getChangedFieldsDetails = (changedFieldsJson) => {
        if (!changedFieldsJson) return [];
        try {
            const parsed = JSON.parse(changedFieldsJson);
            return Object.entries(parsed).map(([field, values]) => ({
                field,
                oldValue: values.old,
                newValue: values.new
            }));
        } catch {
            return [];
        }
    };

    const getEntityLink = (log) => {
        switch (log.entityType) {
            case 'User':
                return `/users/${log.entityId}/edit`;
            case 'Course':
                return `/courses/${log.entityId}`;
            case 'MedicalCertificate':
                return `/users/${log.entityId}`; // Assuming certificate belongs to a user
            case 'Membership':
                return `/users/${log.entityId}`; // Assuming membership belongs to a user
            default:
                return null;
        }
    };

    const getEntityBadgeVariant = (entityType) => {
        switch (entityType) {
            case 'User': return 'primary'; // blue for athletes
            case 'InvitationLink': return 'danger'; // red for invitation links
            case 'Membership': return 'warning'; // yellow for memberships
            case 'Course': return 'success'; // green for courses
            default: return 'secondary';
        }
    };

    if (loading) {
        return (
            <Container className="mt-4 text-center">
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Caricamento...</span>
                </Spinner>
            </Container>
        );
    }

    if (error) {
        return (
            <Container className="mt-4">
                <Alert variant="danger">{error}</Alert>
            </Container>
        );
    }

    return (
        <Container className="mt-4">
            <h2 className="mb-4">
                <i className="fas fa-clipboard-list me-2"></i>
                Log
            </h2>

            {/* Filters */}
            <Card className="mb-4">
                <Card.Body>
                    <Row>
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label>Filtra per entità</Form.Label>
                                <Form.Select
                                    value={filters.entityType}
                                    onChange={(e) => setFilters({ ...filters, entityType: e.target.value })}
                                >
                                    <option value="">Tutti</option>
                                    <option value="User">Atleta</option>
                                    <option value="Course">Corso</option>
                                    <option value="MedicalCertificate">Certificato medico</option>
                                    <option value="Membership">Associazione</option>
                                    <option value="InvitationLink">Link di invito</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label>Numero di risultati</Form.Label>
                                <Form.Select
                                    value={filters.limit}
                                    onChange={(e) => setFilters({ ...filters, limit: parseInt(e.target.value) })}
                                >
                                    <option value="25">25</option>
                                    <option value="50">50</option>
                                    <option value="100">100</option>
                                    <option value="200">200</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Recent Logs Table */}
            <Card>
                <Card.Header>
                    <h5 className="mb-0">Modifiche Recenti</h5>
                </Card.Header>
                <Card.Body className="p-0">
                    <div className="table-responsive">
                        <Table striped hover className="mb-0">
                            <thead>
                                <tr>
                                    <th>Data/Ora</th>
                                    <th>Entità</th>
                                    <th>Azione</th>
                                </tr>
                            </thead>
                            <tbody>
                                {recentLogs.map((log) =>
                                (
                                    <tr key={log.id}>
                                        <td>
                                            <small>{formatDate(log.createdAt)}</small>
                                        </td>
                                        <td>
                                            <OverlayTrigger
                                                placement="top"
                                                overlay={<Tooltip>{log.entityType}</Tooltip>}
                                            >
                                                <Link to={getEntityLink(log)}>
                                                    <Badge bg={getEntityBadgeVariant(log.entityType)} className="me-2">
                                                        {log.entity?.displayName || `${log.entityType} (${log.entityId})`}
                                                    </Badge>
                                                </Link>
                                            </OverlayTrigger>
                                        </td>
                                        <td>{getActionBadge(log)}</td>
                                    </tr>
                                )
                                )}
                            </tbody>
                        </Table>
                    </div>
                </Card.Body>
            </Card>
        </Container>
    );
};

export default AuditDashboard;
